import { z } from 'zod/v4';

/**
 * `GET /api/v1/inbound-routing` — the list envelope.
 *
 * `enabledTotal` counts enabled rules in active projects across ALL pages —
 * the population ingest applies (`InboundRoutingRepository.getEnabledByOrg`).
 * Optional on the wire: an edge-api rolled back alone does not send it, and a
 * reader must then not take one page for the whole list.
 *
 * `scoped` — the list without a `projectId` holds only the projects whose
 * rules the caller may read (role ∧ grant, #2873): its counts are the
 * caller's projects', not the organization's, and say nothing of what ingest
 * does with a record that matches none of them.
 */
export const inboundRoutingListMetaSchema = z.object({
  total: z.number().int().nonnegative(),
  enabledTotal: z.number().int().nonnegative().optional(),
  scoped: z.boolean().optional(),
  limit: z.number().int().nonnegative(),
  offset: z.number().int().nonnegative(),
});

/**
 * Rules are checked for the two fields every reader keys on; the rest of a
 * rule (conditions, actions) passes through to the hand-written UI type.
 */
export const inboundRoutingListResponseSchema = z.object({
  data: z.array(z.looseObject({ id: z.string(), isEnabled: z.boolean() })),
  meta: inboundRoutingListMetaSchema,
});

export type InboundRoutingListMeta = z.infer<typeof inboundRoutingListMetaSchema>;

/**
 * `GET /api/v1/inbound-routing/fallback` (and the `PUT` echo) — the
 * organization's routing fallback. `defaultProjectHidden` — a default project
 * is set but outside the reader's access (#2870): its id comes back `null`,
 * yet unmatched records still go to it. Absent otherwise.
 */
export const inboundRoutingFallbackSettingsSchema = z.object({
  defaultProjectId: z.string().nullable(),
  defaultProjectHidden: z.boolean().optional(),
  rejectUnmatchedRecords: z.boolean(),
});

export const inboundRoutingFallbackResponseSchema = z.object({
  data: inboundRoutingFallbackSettingsSchema,
});

export type InboundRoutingFallbackSettings = z.infer<
  typeof inboundRoutingFallbackSettingsSchema
>;

/**
 * 409 `code` of a create or update whose manual priority another rule of the
 * organization already holds (#2949). The other rule is not named.
 */
export const INBOUND_ROUTING_PRIORITY_TAKEN = 'PRIORITY_TAKEN' as const;

/**
 * 409 `code` of a reorder planned on rules that changed before it was written
 * (#2874): nothing was saved.
 */
export const INBOUND_ROUTING_REORDER_CONFLICT = 'REORDER_CONFLICT' as const;
